/* eslint-disable max-len */
import React, { useState } from 'react';
import { useEffectOnce } from 'react-use';
import styled, { css } from 'styled-components';
import { waitFor } from '../../CodesThatsGottaGoSomewhereElse';
import { LFC } from '../../Legacy';
import { CommandDispatcher } from '../../concepts/commands/CommandDispatcher';
import { Colors, Prompt } from '../../ui';
import { Check } from '../../ui/Check';
import { LoadingBar } from '../../ui/LoadingBar';
import { useResolve } from '../Container';
import { Copy, Flexer, Title } from '../dashboard/elements/LayoutElements';
import { Popup, PopupProps } from '../popups/Popup';
import { boardCreate } from './BoardCommands';

const run = async (dispatch: CommandDispatcher, idList: string[], workspaceId: string, folderId: string, onProgress: (i: number) => void) => {
    for (let i = 0; i < idList.length; i++) {
        await waitFor(500);
        await dispatch(boardCreate, [idList[i], null, null, workspaceId, folderId]);
        onProgress((i + 1) / idList.length);
    }
};

const Step1 = styled.div<{ active: boolean }>`
    position: absolute;
    inset: 0rem;
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: .5rem;

    transition: all .25s cubic-bezier(0,.76,.47,1);
    transform: translateY(-1rem);
    opacity: 0;

    ${p => p.active && css`
        transform: translateY(0);
        opacity: 1;
    `}
`;

const Step2 = styled.div<{ active: boolean }>`
    position: absolute;
    inset: 0rem;
    padding: 1.5rem;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: .5rem;

    transition: .1s transform .25s cubic-bezier(0,.76,.47,1);
    transition: .1s opacity .25s cubic-bezier(0,.76,.47,1);

    transform: scale(.8);
    opacity: 0;
    pointer-events: none;

    ${p => p.active && css`
        transform: scale(1);
        opacity: 1;
        pointer-events: all;
    `}

    &:hover {
        background: ${Colors.hover};
        cursor: pointer;
    }

    &:active {
        background: ${Colors.active};
    }
`;

export type ImportBoardProcessPopupArgs = { list: string[], workspaceId?: string, folderId?: string };

export const importBoardProcessPopup = (args: ImportBoardProcessPopupArgs) => ({ ui: ImportBoardProcessPopup, args });

export const ImportBoardProcessPopup: LFC<PopupProps> = ({ dismiss, args }) => {
    const dispatch = useResolve(CommandDispatcher);
    const { list, workspaceId, folderId } = args as ImportBoardProcessPopupArgs;
    const [progress, setProgress] = useState(0);
    const count = list.length;
    useEffectOnce(() => void (run(dispatch, list, workspaceId, folderId, setProgress)));
    return <Prompt onAction={null}>
        <Popup w={32} h={8} style={{ overflow: 'hidden' }}>
            <Step1 active={progress < 1}>
                <Title flush>{count * progress} / {count}</Title>
                <Flexer shrink width="100%">
                    <LoadingBar progress={progress} />
                </Flexer>
                <Flexer />
                <Copy flush bold color={Colors.textLight}>Please wait while we import your boards..</Copy>
            </Step1>
            <Step2 active={progress >= 1} onClick={dismiss}>
                <Check value />
                <Title size="xl" flush color={Colors.positive}>Import Complete!</Title>
                <Popup.Close onAction={dismiss} />
            </Step2>
        </Popup>
    </Prompt>;
};
